import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ethers } from 'ethers'
import { useWallet } from '../contexts/WalletContext'
import {
  useIsVerifiedCreator,
  useCreateChallenge,
  parseCreateError,
} from '../hooks/useCreateChallenge'
import { generateRoundDeadlines, CHALLENGE_TYPE } from '../types/challenge'
import {
  THEME_EMOJIS,
  THEME_GRADIENTS,
  DEFAULT_EMOJI,
  DEFAULT_COLOR_ID,
  resolveGradient,
} from '../lib/challengeTheme'
import {
  PROOF_TYPES,
  DEFAULT_PROOF_TYPE,
  getProofTypeLabel,
  getProofTypeDesc,
  type ProofType,
} from '../types/challenge'

interface FormState {
  challengeType: 0 | 1 | 2
  name: string
  description: string
  emoji: string
  color: string
  proofType: ProofType
  certCycle: 'daily' | 'weekly'
  startDate: string
  endDate: string
  deadlineTime: string
  minParticipants: string
  maxParticipants: string
  entryFee: string
  prizeEth: string
  winnerCount: string
}

const INITIAL_FORM: FormState = {
  challengeType: 0,
  name: '',
  description: '',
  emoji: DEFAULT_EMOJI,
  color: DEFAULT_COLOR_ID,
  proofType: DEFAULT_PROOF_TYPE,
  certCycle: 'daily',
  startDate: '',
  endDate: '',
  deadlineTime: '23:59',
  minParticipants: '5',
  maxParticipants: '30',
  entryFee: '',
  prizeEth: '',
  winnerCount: '',
}

const STEP_LABELS = ['기본 정보', '규칙 설정', '확인 및 개설'] as const

function StepIndicator({ current }: { current: 1 | 2 | 3 }) {
  return (
    <div
      className="stepper"
      aria-label="단계 표시"
      data-testid="step-indicator"
      style={{ marginBottom: 28 }}
    >
      {STEP_LABELS.map((label, i) => {
        const n = (i + 1) as 1 | 2 | 3
        const isActive = n === current
        const isDone = n < current
        return (
          <div key={n} style={{ display: 'flex', alignItems: 'center' }}>
            <div className={`step${isActive ? ' active' : isDone ? ' done' : ''}`}>
              <div className="step-num">{isDone ? '✓' : n}</div>
              <span className="step-label">{label}</span>
            </div>
            {i < 2 && <div className={`step-bar${isDone ? ' done' : ''}`} />}
          </div>
        )
      })}
    </div>
  )
}

function todayString(): string {
  return new Date().toISOString().split('T')[0]
}

function isValidEth(value: string): boolean {
  if (!value.trim()) return false
  try {
    return ethers.parseEther(value) > 0n
  } catch {
    return false
  }
}

const TYPE_LABELS = ['참가비 챌린지', '홍보 챌린지 — ETH 경품', '홍보 챌린지 — 실물 경품']

export default function CreateChallenge() {
  const { address, isConnected, openConnectModal } = useWallet()
  const navigate = useNavigate()
  const { data: isVerified, isLoading: isVerifiedLoading } = useIsVerifiedCreator(address)
  const { mutate: createChallenge, isPending: isSubmitting } = useCreateChallenge()

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [form, setForm] = useState<FormState>(INITIAL_FORM)
  const [stepError, setStepError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setStepError(null)
  }

  const roundDeadlines = useMemo(() => {
    if (!form.startDate || !form.endDate) return []
    return generateRoundDeadlines(form.startDate, form.endDate, form.certCycle, form.deadlineTime)
  }, [form.startDate, form.endDate, form.certCycle, form.deadlineTime])

  function validateStep1(): string | null {
    if (!form.name.trim()) return '챌린지 이름을 입력해주세요'
    if (form.name.trim().length < 2) return '챌린지 이름은 최소 2자 이상이어야 합니다'
    return null
  }

  function validateStep2(): string | null {
    if (!form.startDate) return '시작일을 선택해주세요'
    if (!form.endDate) return '종료일을 선택해주세요'
    if (form.endDate < form.startDate) return '종료일은 시작일 이후여야 합니다'
    const min = parseInt(form.minParticipants, 10)
    const max = parseInt(form.maxParticipants, 10)
    if (!min || min < 1) return '최소 참가 인원은 1명 이상이어야 합니다'
    if (!max || max < min) return '최대 참가 인원은 최소 인원 이상이어야 합니다'
    if (form.challengeType === CHALLENGE_TYPE.ENTRY_FEE && !isValidEth(form.entryFee))
      return '올바른 참가비(ETH)를 입력해주세요'
    if (form.challengeType === CHALLENGE_TYPE.PROMO_TOKEN && !isValidEth(form.prizeEth))
      return '올바른 경품 ETH 금액을 입력해주세요'
    if (form.challengeType !== CHALLENGE_TYPE.ENTRY_FEE && !(parseInt(form.winnerCount, 10) >= 1))
      return '당첨자 수는 1명 이상이어야 합니다'
    if (roundDeadlines.length === 0) return '라운드가 생성되지 않았습니다. 날짜 범위를 확인해주세요'
    return null
  }

  function goNext() {
    const err = step === 1 ? validateStep1() : validateStep2()
    if (err) {
      setStepError(err)
      return
    }
    setStepError(null)
    setStep((s) => (s < 3 ? ((s + 1) as 1 | 2 | 3) : s))
  }

  function goBack() {
    setStepError(null)
    setSubmitError(null)
    setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3) : s))
  }

  function handleSubmit() {
    setSubmitError(null)
    const startTime = Math.floor(new Date(form.startDate + 'T00:00:00').getTime() / 1000)
    const endTime =
      roundDeadlines.length > 0
        ? roundDeadlines[roundDeadlines.length - 1] + 3600
        : Math.floor(new Date(form.endDate + 'T23:59:59').getTime() / 1000)
    const entryFee =
      form.challengeType === CHALLENGE_TYPE.ENTRY_FEE ? ethers.parseEther(form.entryFee) : 0n
    const prizeEth =
      form.challengeType === CHALLENGE_TYPE.PROMO_TOKEN ? ethers.parseEther(form.prizeEth) : 0n
    const winnerCount =
      form.challengeType !== CHALLENGE_TYPE.ENTRY_FEE ? parseInt(form.winnerCount, 10) : 0

    createChallenge(
      {
        challengeType: form.challengeType,
        name: form.name.trim(),
        startTime,
        endTime,
        minParticipants: parseInt(form.minParticipants, 10),
        maxParticipants: parseInt(form.maxParticipants, 10),
        entryFee,
        prizeEth,
        winnerCount,
        roundDeadlines,
        description: form.description.trim(),
        emoji: form.emoji,
        color: form.color,
        proofType: form.proofType,
      },
      {
        onSuccess: (cloneAddress) => void navigate(`/challenge/${cloneAddress}`),
        onError: (err) => setSubmitError(parseCreateError(err)),
      },
    )
  }

  if (!isConnected) {
    return (
      <div className="empty" style={{ paddingTop: 60 }}>
        <span className="empty-icon">✏️</span>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>챌린지 개설</h2>
        <p style={{ color: 'var(--ink-2)', margin: 0 }}>
          챌린지를 개설하려면 지갑을 연결해야 합니다.
        </p>
        <button className="btn btn-primary" onClick={openConnectModal}>
          지갑 연결
        </button>
        <Link to="/" className="btn btn-quiet btn-sm">
          ← 목록으로
        </Link>
      </div>
    )
  }

  const typeOptions = [
    {
      type: CHALLENGE_TYPE.ENTRY_FEE,
      label: '참가비 챌린지',
      desc: '참가자들이 참가비를 내고 완주자에게 균등 분배',
      disabled: false,
    },
    {
      type: CHALLENGE_TYPE.PROMO_TOKEN,
      label: '홍보 챌린지 — ETH 경품',
      desc: '개설자가 ETH를 예치하고 완주자 중 랜덤 추첨',
      disabled: false,
    },
    {
      type: CHALLENGE_TYPE.PROMO_PHYSICAL,
      label: '홍보 챌린지 — 실물 경품',
      desc: isVerifiedLoading
        ? '자격 확인 중...'
        : isVerified
          ? '인증 개설자 전용 — 당첨자 주소만 온체인 기록'
          : '인증 개설자만 선택 가능 (관리자 심사 필요)',
      disabled: !isVerified,
    },
  ]

  const renderStep1 = () => (
    <div data-testid="step-1">
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 20px' }}>
        챌린지 기본 정보를 입력하세요
      </h2>
      <div className="field" style={{ marginBottom: 18 }}>
        <label className="field-label">챌린지 타입</label>
        <div className="radio-cards" role="radiogroup" aria-label="챌린지 타입 선택">
          {typeOptions.map((opt) => (
            <div
              key={opt.type}
              className={`radio-card${form.challengeType === opt.type ? ' sel' : ''}${opt.disabled ? ' disabled' : ''}`}
              onClick={() => {
                if (!opt.disabled) update('challengeType', opt.type as 0 | 1 | 2)
              }}
              role="radio"
              aria-checked={form.challengeType === opt.type}
              aria-disabled={opt.disabled}
              data-testid={`radio-type-${opt.type}`}
            >
              <div className="radio-dot" />
              <div>
                <div className="radio-title">{opt.label}</div>
                <div className="radio-desc">{opt.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="field" style={{ marginBottom: 18 }}>
        <label className="field-label" htmlFor="field-name">
          챌린지 이름 <span className="req">*</span>
        </label>
        <input
          id="field-name"
          type="text"
          className="input"
          placeholder="예: 30일 아침 운동 챌린지"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          maxLength={60}
          aria-label="챌린지 이름"
          data-testid="field-name"
        />
        <span className="field-hint">{form.name.length}/60자</span>
      </div>
      <div className="field">
        <label className="field-label" htmlFor="field-description">
          목표 설명
        </label>
        <textarea
          id="field-description"
          className="textarea"
          placeholder="챌린지 목표와 인증 방법을 설명해주세요..."
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          maxLength={2000}
          aria-label="목표 설명"
          data-testid="field-description"
        />
        <span className="field-hint">{form.description.length}/2000자 · 오프체인 저장</span>
      </div>

      {/* Thumbnail theme: emoji + color */}
      <div className="field" style={{ marginTop: 18 }}>
        <label className="field-label">대표 이모지 & 색상</label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            marginBottom: 12,
            padding: 12,
            borderRadius: 'var(--radius)',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
          }}
        >
          <div
            aria-hidden
            style={{
              width: 56,
              height: 56,
              borderRadius: 'var(--radius)',
              background: resolveGradient(form.color, 0),
              display: 'grid',
              placeItems: 'center',
              fontSize: 28,
              flexShrink: 0,
            }}
          >
            {form.emoji}
          </div>
          <span className="field-hint" style={{ margin: 0 }}>
            목록 카드에 이렇게 표시됩니다.
          </span>
        </div>

        <div
          role="radiogroup"
          aria-label="대표 이모지 선택"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}
        >
          {THEME_EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              role="radio"
              aria-checked={form.emoji === e}
              aria-label={`이모지 ${e}`}
              onClick={() => update('emoji', e)}
              style={{
                width: 38,
                height: 38,
                fontSize: 20,
                lineHeight: 1,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                background: form.emoji === e ? 'var(--primary-weak)' : 'var(--surface)',
                border: `1.5px solid ${form.emoji === e ? 'var(--primary)' : 'var(--border-2)'}`,
              }}
            >
              {e}
            </button>
          ))}
        </div>

        <div role="radiogroup" aria-label="대표 색상 선택" style={{ display: 'flex', gap: 8 }}>
          {THEME_GRADIENTS.map((g) => (
            <button
              key={g.id}
              type="button"
              role="radio"
              aria-checked={form.color === g.id}
              aria-label={`색상 ${g.id}`}
              onClick={() => update('color', g.id)}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                cursor: 'pointer',
                background: g.css,
                border: `2px solid ${form.color === g.id ? 'var(--ink)' : 'transparent'}`,
                boxShadow: form.color === g.id ? '0 0 0 2px var(--surface)' : 'none',
                outline: form.color === g.id ? '2px solid var(--ink)' : 'none',
                outlineOffset: 1,
              }}
            />
          ))}
        </div>
      </div>

      {/* Proof method */}
      <div className="field" style={{ marginTop: 18 }}>
        <label className="field-label">인증 방식</label>
        <div className="radio-cards" role="radiogroup" aria-label="인증 방식 선택">
          {PROOF_TYPES.map((pt) => (
            <div
              key={pt}
              className={`radio-card${form.proofType === pt ? ' sel' : ''}`}
              onClick={() => update('proofType', pt)}
              role="radio"
              aria-checked={form.proofType === pt}
              data-testid={`radio-proof-${pt}`}
            >
              <div className="radio-dot" />
              <div>
                <div className="radio-title">{getProofTypeLabel(pt)}</div>
                <div className="radio-desc">{getProofTypeDesc(pt)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )

  const renderStep2 = () => (
    <div data-testid="step-2">
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 20px' }}>
        챌린지 규칙을 설정하세요
      </h2>
      <div className="field" style={{ marginBottom: 18 }}>
        <label className="field-label">인증 주기</label>
        <div style={{ display: 'flex', gap: 12 }}>
          {(['daily', 'weekly'] as const).map((cycle) => (
            <label
              key={cycle}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              <input
                type="radio"
                name="certCycle"
                value={cycle}
                checked={form.certCycle === cycle}
                onChange={() => update('certCycle', cycle)}
              />
              {cycle === 'daily' ? '매일' : '매주'}
            </label>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>
        <div className="field">
          <label className="field-label" htmlFor="field-start-date">
            시작일 <span className="req">*</span>
          </label>
          <input
            id="field-start-date"
            type="date"
            className="input"
            min={todayString()}
            value={form.startDate}
            onChange={(e) => update('startDate', e.target.value)}
            aria-label="시작일"
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="field-end-date">
            종료일 <span className="req">*</span>
          </label>
          <input
            id="field-end-date"
            type="date"
            className="input"
            min={form.startDate || todayString()}
            value={form.endDate}
            onChange={(e) => update('endDate', e.target.value)}
            aria-label="종료일"
          />
        </div>
      </div>
      <div className="field" style={{ marginBottom: 18 }}>
        <label className="field-label" htmlFor="field-deadline-time">
          라운드 마감 시각
        </label>
        <input
          id="field-deadline-time"
          type="time"
          className="input"
          style={{ maxWidth: 160 }}
          value={form.deadlineTime}
          onChange={(e) => update('deadlineTime', e.target.value)}
          aria-label="라운드 마감 시각"
        />
        {roundDeadlines.length > 0 && (
          <span className="field-hint">
            총 {roundDeadlines.length}라운드 생성됨 ({form.certCycle === 'daily' ? '매일' : '매주'}{' '}
            인증)
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>
        <div className="field">
          <label className="field-label" htmlFor="field-min-participants">
            최소 참가 인원
          </label>
          <input
            id="field-min-participants"
            type="number"
            className="input"
            min={1}
            value={form.minParticipants}
            onChange={(e) => update('minParticipants', e.target.value)}
            aria-label="최소 참가 인원"
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="field-max-participants">
            최대 참가 인원
          </label>
          <input
            id="field-max-participants"
            type="number"
            className="input"
            min={1}
            value={form.maxParticipants}
            onChange={(e) => update('maxParticipants', e.target.value)}
            aria-label="최대 참가 인원"
          />
        </div>
      </div>
      {form.challengeType === CHALLENGE_TYPE.ENTRY_FEE && (
        <div className="field" style={{ marginBottom: 18 }}>
          <label className="field-label" htmlFor="field-entry-fee">
            참가비 (ETH) <span className="req">*</span>
          </label>
          <div className="input-affix">
            <input
              id="field-entry-fee"
              type="number"
              step="0.001"
              min="0"
              className="input"
              placeholder="0.01"
              value={form.entryFee}
              onChange={(e) => update('entryFee', e.target.value)}
              aria-label="참가비"
            />
            <span className="suffix">ETH</span>
          </div>
          <span className="field-hint">참가비는 완주자에게 균등 분배됩니다 (수수료 3% 공제)</span>
        </div>
      )}
      {form.challengeType === CHALLENGE_TYPE.PROMO_TOKEN && (
        <div className="field" style={{ marginBottom: 18 }}>
          <label className="field-label" htmlFor="field-prize-eth">
            경품 ETH 금액 <span className="req">*</span>
          </label>
          <div className="input-affix">
            <input
              id="field-prize-eth"
              type="number"
              step="0.001"
              min="0"
              className="input"
              placeholder="0.5"
              value={form.prizeEth}
              onChange={(e) => update('prizeEth', e.target.value)}
              aria-label="경품 ETH 금액"
            />
            <span className="suffix">ETH</span>
          </div>
          <span className="field-hint">개설 시 컨트랙트에 즉시 예치됩니다</span>
        </div>
      )}
      {form.challengeType !== CHALLENGE_TYPE.ENTRY_FEE && (
        <div className="field">
          <label className="field-label" htmlFor="field-winner-count">
            당첨자 수 <span className="req">*</span>
          </label>
          <input
            id="field-winner-count"
            type="number"
            min={1}
            className="input"
            placeholder="3"
            value={form.winnerCount}
            onChange={(e) => update('winnerCount', e.target.value)}
            aria-label="당첨자 수"
          />
        </div>
      )}
    </div>
  )

  const renderStep3 = () => (
    <div data-testid="step-3">
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 20px' }}>
        입력 내용을 확인하고 개설하세요
      </h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <div
          aria-hidden
          style={{
            width: 52,
            height: 52,
            borderRadius: 'var(--radius)',
            background: resolveGradient(form.color, 0),
            display: 'grid',
            placeItems: 'center',
            fontSize: 26,
            flexShrink: 0,
          }}
        >
          {form.emoji}
        </div>
        <div style={{ fontWeight: 700, fontSize: 15 }}>{form.name}</div>
      </div>
      <div className="info-rows" style={{ marginBottom: 20 }}>
        <div className="info-row">
          <span className="k">타입</span>
          <span className="v">{TYPE_LABELS[form.challengeType]}</span>
        </div>
        <div className="info-row">
          <span className="k">이름</span>
          <span className="v">{form.name}</span>
        </div>
        {form.description && (
          <div className="info-row">
            <span className="k">설명</span>
            <span className="v" style={{ fontSize: 13 }}>
              {form.description.slice(0, 80)}
              {form.description.length > 80 ? '…' : ''}
            </span>
          </div>
        )}
        <div className="info-row">
          <span className="k">인증 주기</span>
          <span className="v">{form.certCycle === 'daily' ? '매일' : '매주'}</span>
        </div>
        <div className="info-row">
          <span className="k">기간</span>
          <span className="v">
            {form.startDate} ~ {form.endDate}
          </span>
        </div>
        <div className="info-row">
          <span className="k">총 라운드</span>
          <span className="v" style={{ color: 'var(--primary)' }}>
            {roundDeadlines.length}라운드
          </span>
        </div>
        <div className="info-row">
          <span className="k">참가 인원</span>
          <span className="v">
            최소 {form.minParticipants}명 / 최대 {form.maxParticipants}명
          </span>
        </div>
        {form.challengeType === CHALLENGE_TYPE.ENTRY_FEE && (
          <div className="info-row">
            <span className="k">참가비</span>
            <span className="v eth-amt">{form.entryFee} ETH</span>
          </div>
        )}
        {form.challengeType === CHALLENGE_TYPE.PROMO_TOKEN && (
          <div className="info-row">
            <span className="k">경품 예치</span>
            <span className="v eth-amt">{form.prizeEth} ETH</span>
          </div>
        )}
        {form.challengeType !== CHALLENGE_TYPE.ENTRY_FEE && (
          <div className="info-row">
            <span className="k">당첨자 수</span>
            <span className="v">{form.winnerCount}명</span>
          </div>
        )}
      </div>
      {form.challengeType === CHALLENGE_TYPE.PROMO_TOKEN && (
        <div className="alert alert-warn" style={{ marginBottom: 12 }}>
          ⚠️ 개설 트랜잭션 시 <strong>{form.prizeEth} ETH</strong>가 컨트랙트에 예치됩니다.
        </div>
      )}
      {form.challengeType === CHALLENGE_TYPE.ENTRY_FEE ? (
        <div className="field-hint" style={{ marginBottom: 4 }}>
          · 개설 시 <strong>참가비가 함께 결제되어 개설자가 자동 참가</strong>됩니다 (트랜잭션 1회).
          시작 시각이 지나면 자동으로 진행되고, 최소 인원 미달이면 자동으로 환불 가능 상태가 됩니다
        </div>
      ) : (
        <div className="field-hint" style={{ marginBottom: 4 }}>
          · 개설 시 <strong>경품이 컨트랙트에 예치</strong>됩니다 (트랜잭션 1회)
        </div>
      )}
      <div className="field-hint">· 목표 설명은 오프체인(Cloudflare KV)에 저장됩니다</div>
    </div>
  )

  return (
    <div className="page-narrow">
      <Link to="/" className="btn btn-quiet btn-sm" style={{ marginBottom: 20, paddingLeft: 0 }}>
        ← 목록으로
      </Link>
      <div className="pg-head">
        <h1 className="pg-title">챌린지 개설</h1>
      </div>
      <StepIndicator current={step} />
      <div className="card card-pad">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {(stepError || submitError) && (
          <div className="alert alert-danger" role="alert" style={{ marginTop: 16 }}>
            {stepError || submitError}
          </div>
        )}
        <div className="divider" />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {step === 1 ? (
            <Link to="/" className="btn btn-quiet btn-sm">
              취소
            </Link>
          ) : (
            <button className="btn btn-ghost" onClick={goBack} disabled={isSubmitting}>
              이전으로
            </button>
          )}
          {step < 3 ? (
            <button className="btn btn-primary" onClick={goNext} data-testid="btn-next">
              다음
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
              data-testid="btn-submit"
            >
              {isSubmitting ? '개설 중…' : '개설하기'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
